import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Home, ShoppingCart, Gamepad2, Crown, Gift } from 'lucide-react';

const ACCESS_KEY = '/emoji/key.png';

/** Aperçu Access → onglet Accès ; shop validé → Global Pack */
function buildTabs(previewMode) {
  return [
    { id: 'home', icon: Home, label: 'Accueil', view: 'home' },
    { id: 'cart', icon: ShoppingCart, labelKey: 'cart', view: 'cart' },
    { id: 'game', icon: Gamepad2, label: 'Jeu', action: 'arcade' },
    previewMode
      ? { id: 'access', label: 'Accès', action: 'access', keyIcon: true }
      : { id: 'pack', icon: Gift, label: 'G. Pack', view: 'pack' },
    { id: 'pass', icon: Crown, labelKey: 'subscriptions', action: 'subscription' },
  ];
}

function haptic(style = 'light') {
  try {
    const hf = window.Telegram?.WebApp?.HapticFeedback;
    if (style === 'success' && hf?.notificationOccurred) hf.notificationOccurred('success');
    else if (hf?.impactOccurred) hf.impactOccurred(style === 'medium' ? 'medium' : 'light');
  } catch { /* */ }
}

function NavBadge({ count, tone = 'red' }) {
  if (!count || count <= 0) return null;
  const bg = tone === 'green'
    ? 'linear-gradient(135deg, #22c55e, #16a34a)'
    : 'linear-gradient(135deg, #ef4444, #dc2626)';
  const shadow = tone === 'green' ? 'rgba(34,197,94,0.55)' : 'rgba(239,68,68,0.55)';
  return (
    <span
      className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full flex items-center justify-center text-[10px] font-black text-white leading-none"
      style={{ background: bg, boxShadow: `0 2px 8px ${shadow}` }}
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

function isTabActive(tab, currentView) {
  if (tab.action) return false;
  if (tab.view === 'cart') return currentView === 'cart' || currentView === 'cart-summary';
  if (tab.view === 'pack') return currentView === 'pack';
  return currentView === tab.view;
}

function tabBadge(tab, cart, freePacks) {
  if (tab.id === 'cart') return cart?.length || 0;
  if (tab.id === 'pack') return freePacks > 0 ? freePacks : 0;
  return 0;
}

function tabBadgeTone(tab) {
  return tab.id === 'pack' ? 'green' : 'red';
}

function NavTab({ tab, active, label, onPress, badge, badgeTone, locked }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={locked ? `${label} (verrouillé)` : label}
      aria-current={active ? 'page' : undefined}
      className="relative flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-1.5 rounded-2xl transition-all duration-250"
      style={{
        background: active
          ? 'linear-gradient(135deg, rgba(74,144,226,0.55) 0%, rgba(107,163,232,0.42) 100%)'
          : 'transparent',
        border: active ? '1px solid rgba(147,197,253,0.45)' : '1px solid transparent',
        boxShadow: active ? '0 4px 16px rgba(74,144,226,0.35), inset 0 1px 0 rgba(255,255,255,0.2)' : 'none',
        transform: active ? 'scale(1.02)' : 'scale(1)',
        opacity: locked ? 0.55 : 1,
      }}
    >
      <span className="relative flex items-center justify-center w-9 h-9">
        {tab.keyIcon ? (
          <img
            src={ACCESS_KEY}
            alt=""
            style={{
              width: 26, height: 26, objectFit: 'contain',
              filter: active
                ? 'drop-shadow(0 0 10px rgba(74,144,226,0.75))'
                : 'drop-shadow(0 0 6px rgba(74,144,226,0.35))',
            }}
          />
        ) : (
          <Icon
            className="w-[22px] h-[22px]"
            strokeWidth={active ? 2.25 : 2}
            style={{
              color: locked ? 'rgba(248,113,113,0.85)' : (active ? '#ffffff' : 'rgba(191,219,254,0.75)'),
              filter: active && !locked ? 'drop-shadow(0 0 8px rgba(147,197,253,0.7))' : 'none',
            }}
          />
        )}
        {locked ? (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-black"
            style={{ background: '#ef4444', color: '#fff', lineHeight: 1 }}
          >
            🔒
          </span>
        ) : (
          <NavBadge count={badge} tone={badgeTone} />
        )}
      </span>
      <span
        className="text-[10px] font-semibold truncate max-w-full px-0.5 leading-tight"
        style={{ color: locked ? 'rgba(252,165,165,0.85)' : (active ? '#ffffff' : 'rgba(191,219,254,0.65)') }}
      >
        {label}
      </span>
    </button>
  );
}

const glassBarStyle = (isScrolled, mobile) => ({
  background: isScrolled
    ? mobile
      ? 'linear-gradient(135deg, rgba(74,144,226,0.32) 0%, rgba(30,60,140,0.48) 50%, rgba(10,20,80,0.55) 100%)'
      : 'linear-gradient(135deg, rgba(74,144,226,0.30) 0%, rgba(30,60,140,0.44) 50%, rgba(10,20,80,0.52) 100%)'
    : mobile
      ? 'linear-gradient(135deg, rgba(74,144,226,0.22) 0%, rgba(30,60,140,0.38) 50%, rgba(10,20,80,0.45) 100%)'
      : 'linear-gradient(135deg, rgba(74,144,226,0.18) 0%, rgba(30,60,140,0.32) 50%, rgba(10,20,80,0.40) 100%)',
  backdropFilter: 'blur(40px) saturate(280%) brightness(1.08)',
  WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.08)',
  border: '1.5px solid rgba(107,163,232,0.38)',
  boxShadow: mobile
    ? '0 -2px 28px rgba(74,144,226,0.2), 0 10px 36px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.28)'
    : '0 4px 28px rgba(74,144,226,0.16), inset 0 1px 0 rgba(255,255,255,0.26)',
});

const NAV_HEIGHT_CSS = 'var(--device-nav-height, calc(96px + env(safe-area-inset-bottom, 0px)))';

export function MobileNavBottomSpacer() {
  return (
    <div
      aria-hidden="true"
      style={{ height: NAV_HEIGHT_CSS, flexShrink: 0, width: '100%', pointerEvents: 'none' }}
    />
  );
}

export function MobileNavBottomReserve() {
  return (
    <div
      aria-hidden="true"
      style={{ height: NAV_HEIGHT_CSS, flexShrink: 0, width: '100%', pointerEvents: 'none' }}
    />
  );
}

/** Swipe Commander — liquid glass aligné app */
export function SwipeCommanderButton({
  onComplete,
  label = 'Commander',
  hint = 'Glisse pour valider ta commande',
  disabled = false,
  className = '',
}) {
  const trackRef = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [success, setSuccess] = useState(false);
  const startX = useRef(0);
  const maxX = useRef(0);

  const knobSize = 56;
  const pad = 6;
  const progress = maxX.current > 0 ? Math.min(1, dragX / maxX.current) : 0;

  const measure = () => {
    const el = trackRef.current;
    if (!el) return 0;
    return Math.max(0, el.clientWidth - knobSize - pad * 2);
  };

  const finish = useCallback(() => {
    setSuccess(true);
    haptic('success');
    setTimeout(() => {
      setSuccess(false);
      setDragX(0);
      setDragging(false);
      if (typeof onComplete === 'function') onComplete();
    }, 280);
  }, [onComplete]);

  const onPointerDown = (e) => {
    if (disabled || success) return;
    e.preventDefault();
    maxX.current = measure();
    startX.current = e.clientX - dragX;
    setDragging(true);
    haptic('light');
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* */ }
  };

  const onPointerMove = (e) => {
    if (!dragging || disabled) return;
    setDragX(Math.min(maxX.current, Math.max(0, e.clientX - startX.current)));
  };

  const onPointerUp = () => {
    if (!dragging) return;
    if (dragX >= maxX.current * 0.68) {
      setDragX(maxX.current);
      finish();
    } else {
      setDragX(0);
      setDragging(false);
    }
  };

  return (
    <div className={className} style={{ width: '100%' }}>
      {hint ? (
        <p style={{
          margin: '0 0 10px', textAlign: 'center', fontSize: 12, fontWeight: 700,
          color: 'rgba(191,219,254,0.72)', letterSpacing: '0.01em',
        }}>
          {hint}
        </p>
      ) : null}
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          width: '100%',
          height: 64,
          borderRadius: 18,
          overflow: 'hidden',
          touchAction: 'none',
          userSelect: 'none',
          opacity: disabled ? 0.55 : 1,
          transform: dragging ? 'scale(0.985)' : 'scale(1)',
          transition: 'transform 0.15s ease',
          background: success
            ? 'linear-gradient(135deg, rgba(96,165,250,0.55) 0%, rgba(37,99,235,0.62) 100%)'
            : 'linear-gradient(135deg, rgba(74,144,226,0.22) 0%, rgba(30,60,140,0.38) 55%, rgba(10,20,80,0.42) 100%)',
          backdropFilter: 'blur(40px) saturate(280%) brightness(1.1)',
          WebkitBackdropFilter: 'blur(40px) saturate(280%) brightness(1.1)',
          border: '1.5px solid rgba(147,197,253,0.45)',
          boxShadow: '0 10px 36px rgba(37,99,235,0.28), 0 0 28px rgba(74,144,226,0.15), inset 0 1px 0 rgba(255,255,255,0.28)',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${Math.max(16, progress * 100)}%`,
            background: 'linear-gradient(90deg, rgba(147,197,253,0.38), rgba(96,165,250,0.08))',
            pointerEvents: 'none',
            transition: dragging ? 'none' : 'width 0.25s ease',
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute', top: 0, left: '10%', right: '10%', height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55) 50%, transparent)',
            pointerEvents: 'none',
          }}
        />
        <span
          style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            paddingLeft: knobSize + 8, paddingRight: 14, fontSize: 15, fontWeight: 900, color: '#fff',
            letterSpacing: '0.04em', textTransform: 'uppercase',
            pointerEvents: 'none', opacity: progress > 0.55 ? 0.35 : 1,
            textShadow: '0 2px 12px rgba(0,0,0,0.35)',
          }}
        >
          {success ? 'Accès…' : label}
        </span>
        <div
          role="slider"
          aria-label={label}
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            position: 'absolute',
            left: pad + dragX,
            top: '50%',
            width: knobSize,
            height: knobSize,
            marginTop: -knobSize / 2,
            borderRadius: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : (dragging ? 'grabbing' : 'grab'),
            background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(191,219,254,0.92) 100%)',
            border: '1.5px solid rgba(147,197,253,0.7)',
            boxShadow: '0 6px 20px rgba(37,99,235,0.4), inset 0 1px 0 rgba(255,255,255,0.85)',
            transition: dragging ? 'none' : 'left 0.25s cubic-bezier(0.22,1,0.36,1)',
            zIndex: 2,
            color: '#1e3a5f',
            fontWeight: 900,
            fontSize: 22,
          }}
        >
          →
        </div>
      </div>
    </div>
  );
}

export function AppNavbarMobile({
  currentView,
  setCurrentView,
  cart,
  freePacks,
  t,
  setShowArcadeModal,
  setShowSubscriptionModal,
  trackEvent,
  isScrolled,
  previewLocked = false,
  onLockedNav,
  onAccessNav,
}) {
  const tabs = useMemo(() => buildTabs(!!previewLocked), [previewLocked]);

  const onTab = (tab) => {
    if (previewLocked && (tab.id === 'cart' || tab.id === 'pass')) {
      if (typeof onLockedNav === 'function') onLockedNav();
      return;
    }
    if (tab.action === 'access') {
      if (typeof onAccessNav === 'function') onAccessNav();
      return;
    }
    if (tab.action === 'arcade') {
      setShowArcadeModal(true);
      return;
    }
    if (tab.action === 'subscription') {
      setShowSubscriptionModal(true);
      trackEvent('pass_click');
      return;
    }
    if (tab.view === 'pack') trackEvent?.('pack_nav_click');
    setCurrentView(tab.view);
  };

  const nav = (
    <nav
      className="gs-mobile-nav-fixed md:hidden flex justify-center items-end pointer-events-none"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 'var(--gs-z-nav, 50)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
      }}
      aria-label="Navigation principale"
    >
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        className="relative mx-3 mb-2 px-2 py-2 pointer-events-auto w-[calc(100%-24px)] max-w-[420px]"
        style={{ ...glassBarStyle(isScrolled, true), borderRadius: '22px' }}
      >
        <div
          style={{
            position: 'absolute', top: 0, left: '8%', right: '8%', height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(147,197,253,0.65) 50%, transparent)',
            borderRadius: '22px 22px 0 0', pointerEvents: 'none',
          }}
        />
        <div className="flex items-stretch gap-0.5 relative z-10">
          {tabs.map((tab) => {
            const locked = previewLocked && (tab.id === 'cart' || tab.id === 'pass');
            return (
              <NavTab
                key={tab.id}
                tab={tab}
                active={!locked && isTabActive(tab, currentView)}
                label={tab.labelKey ? t(tab.labelKey) : tab.label}
                onPress={() => onTab(tab)}
                badge={locked ? 0 : tabBadge(tab, cart, freePacks)}
                badgeTone={tabBadgeTone(tab)}
                locked={locked}
              />
            );
          })}
        </div>
      </motion.div>
    </nav>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(nav, document.body);
  }
  return nav;
}

export function AppNavbarDesktop({
  currentView,
  setCurrentView,
  cart,
  freePacks,
  t,
  setShowArcadeModal,
  setShowSubscriptionModal,
  trackEvent,
  isScrolled,
  userPass,
  previewLocked = false,
  onLockedNav,
  onAccessNav,
}) {
  const tabs = useMemo(() => buildTabs(!!previewLocked), [previewLocked]);

  const onTab = (tab) => {
    if (previewLocked && (tab.id === 'cart' || tab.id === 'pass')) {
      if (typeof onLockedNav === 'function') onLockedNav();
      return;
    }
    if (tab.action === 'access') {
      if (typeof onAccessNav === 'function') onAccessNav();
      return;
    }
    if (tab.action === 'arcade') { setShowArcadeModal(true); return; }
    if (tab.action === 'subscription') { setShowSubscriptionModal(true); trackEvent('pass_click'); return; }
    if (tab.view === 'pack') trackEvent?.('pack_nav_click');
    setCurrentView(tab.view);
  };

  return (
    <nav className="mb-6 hidden md:block">
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="relative px-3 py-2.5"
        style={{ ...glassBarStyle(isScrolled, false), borderRadius: '18px' }}
      >
        <div
          style={{
            position: 'absolute', top: 0, left: '5%', right: '5%', height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(107,163,232,0.65) 50%, transparent)',
            borderRadius: '18px 18px 0 0', pointerEvents: 'none',
          }}
        />
        <div className="flex items-center justify-center gap-2 flex-wrap relative z-10">
          {tabs.map((tab) => {
            const locked = previewLocked && (tab.id === 'cart' || tab.id === 'pass');
            const active = !locked && isTabActive(tab, currentView);
            const Icon = tab.icon;
            const label = tab.labelKey ? t(tab.labelKey) : tab.label;
            const badge = locked ? 0 : tabBadge(tab, cart, freePacks);
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTab(tab)}
                className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all duration-200"
                style={{
                  background: active
                    ? 'linear-gradient(135deg, rgba(74,144,226,0.55) 0%, rgba(107,163,232,0.45) 100%)'
                    : 'rgba(255,255,255,0.04)',
                  border: locked
                    ? '1px solid rgba(248,113,113,0.35)'
                    : (active ? '1px solid rgba(147,197,253,0.42)' : '1px solid rgba(107,163,232,0.15)'),
                  color: locked ? 'rgba(252,165,165,0.9)' : (active ? '#fff' : 'rgba(191,219,254,0.8)'),
                  boxShadow: active ? '0 4px 16px rgba(74,144,226,0.3)' : 'none',
                  opacity: locked ? 0.7 : 1,
                }}
              >
                <span className="relative">
                  {tab.keyIcon ? (
                    <img src={ACCESS_KEY} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  ) : (
                    <Icon className="w-5 h-5" strokeWidth={active ? 2.25 : 2} />
                  )}
                  {locked ? (
                    <span className="absolute -top-1 -right-1 text-[9px]">🔒</span>
                  ) : (
                    <NavBadge count={badge} tone={tabBadgeTone(tab)} />
                  )}
                </span>
                <span>{label}</span>
                {!locked && tab.id === 'pass' && userPass && userPass !== 'bronze' && (
                  <span style={{
                    fontSize: 9, fontWeight: 900, borderRadius: 100, padding: '2px 6px',
                    background: userPass === 'or' ? 'linear-gradient(135deg,#f59e0b,#fbbf24)' : 'linear-gradient(135deg,#4a90e2,#6ba3e8)',
                    color: userPass === 'or' ? '#000' : '#fff',
                  }}
                  >
                    {userPass === 'or' ? 'OR' : 'AG'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </motion.div>
    </nav>
  );
}
